import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ProductVariant from '@/shared/types/products/productVariant.type';

const getPriceByCurrency = (product: ProductVariant, currency: string) => {
  const { product_prices, value } = product;

  if (product_prices) {
    return (
      product_prices.find((item) => item.currency_code === currency)?.value ||
      product_prices[0].value
    );
  }

  return value.toFixed(2);
};

type ProductItemDetailsPopoverProps = {
  product: ProductVariant;
  currency: string;
  onRemoveSelectedProduct: (id: string) => void;
  onUpdateSelectedProducts: (product: ProductVariant) => void;
  onCloseDetailsPopover: () => void;
};

const ProductItemDetailsPopover: FC<ProductItemDetailsPopoverProps> = ({
  product,
  currency,
  onRemoveSelectedProduct,
  onUpdateSelectedProducts,
  onCloseDetailsPopover,
}) => {
  const { t } = useTranslation(['orders', 'common']);

  const [formValues, setFormValues] = useState({
    price: getPriceByCurrency(product, currency),
    product_qty: product.product_qty,
  });

  const handleApplyClick = () => {
    if (product.product_prices) {
      onUpdateSelectedProducts({
        ...product,
        product_qty: parseInt(formValues.product_qty as string),
        product_prices: product?.product_prices.map((item) =>
          item.currency_code === currency
            ? { ...item, value: formValues.price }
            : item,
        ),
      });
    } else {
      onUpdateSelectedProducts({
        ...product,
        product_qty: parseInt(formValues.product_qty as string),
        value: parseFloat(formValues.price),
      });
    }
    onCloseDetailsPopover();
  };

  return (
    <Stack spacing={2}>
      <Typography variant='body1' sx={{ mb: 2 }}>
        {product.product_name}
      </Typography>

      <TextField
        label={t('orders:price')}
        placeholder={t('orders:price')}
        inputProps={{ type: 'number' }}
        value={formValues.price}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            price: e.target.value,
          }))
        }
      />

      <TextField
        label={t('orders:quantity')}
        placeholder={t('orders:quantity')}
        inputProps={{ type: 'number' }}
        value={formValues.product_qty}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            product_qty: e.target.value,
          }))
        }
      />

      <Button variant='contained' onClick={handleApplyClick}>
        {t('common:apply')}
      </Button>

      <Button
        variant='contained'
        color='error'
        onClick={() => onRemoveSelectedProduct(product.variant_id)}
      >
        {t('common:delete')}
      </Button>
    </Stack>
  );
};

export default ProductItemDetailsPopover;
